import { differenceInSeconds } from 'date-fns';
import { useEffect } from 'react';

import { useClock } from '@/lib/now.ts';

import type { NoDate } from '../services/new-utils.ts';

/**
 * Checks if now is after given **UTC** date.
 *
 * Prefer creating dates using `createDateInNorwegianTime()` function.
 *
 * Date string **MUST** be in UTC format, otherwise this function will
 * provide different results in different timezones.
 *
 * Example: "2024-03-27T12:00:00.000Z" <- notice this 'Z' at the end.
 *
 * This hook will refresh UI automatically.
 *
 * @param date - UTC date string
 */
export const useIsAfterDate = (date: string | NoDate): boolean => {
  if (import.meta.env.DEV) {
    if (typeof date === 'string') {
      if (!date.endsWith('Z')) {
        throw new Error(
          'Incorrect date format used, use ISO string in UTC timezone. Other formats will cause bugs.',
        );
      }
    }
  }

  const [now, refresh] = useClock();
  const isAfter = differenceInSeconds(date, now) < 0;

  useEffect(() => {
    let timeout: number;

    const check = () => {
      // Measured against the real clock, not `now`, so a stale render still notices the cutoff.
      const diff = differenceInSeconds(date, Date.now());

      if (isAfter !== diff < 0) {
        refresh();
      }

      const delay = getNextDelay(diff);

      if (delay) {
        timeout = window.setTimeout(check, delay);
      }
    };

    check();

    return () => {
      clearTimeout(timeout);
    };
  }, [date, isAfter, refresh]);

  return isAfter;
};

const getNextDelay = (diff: number) => {
  if (diff < 0) {
    // No need to run checks after condition is met
    return undefined;
  } else if (diff < 10) {
    return 200;
  } else if (diff < 80) {
    return 1000;
  } else if (diff < 300) {
    return 10_000;
  }

  return 60_000;
};
